/**
 * Article Slug Mapping
 * Maps an article's internal slug (`Article.slug` in src/data/articles.ts) to the
 * URL slug used in each language.
 *
 * Most articles use the same slug in both languages — English slug at
 * `/articles/:slug`, same slug under the `/nl/` prefix. Articles written for a
 * Dutch keyword (published from `content/queue.yaml` by the content routine) keep
 * that Dutch keyword on `/nl/articles/...` and get a readable English slug on
 * `/articles/...`, so the English URL is not a Dutch phrase.
 *
 * Only add an entry when the two languages differ; `getLocalizedArticleSlug()`
 * falls back to the internal slug for everything else. The internal slug stays
 * the key, so `src/data/articles.ts` never has to be re-keyed.
 *
 * Any slug changed here for an already-published article needs a 301 in
 * vercel.json from the old URL to the new one. Every entry also needs a 301
 * from `/articles/<nl>` to `/articles/<en>`.
 */

export interface ArticleSlugMap {
  en: string;
  nl: string;
}

/**
 * Articles whose English and Dutch URL slugs differ.
 * Key = internal slug (also the Dutch slug for every entry below).
 */
export const articleSlugMap: Record<string, ArticleSlugMap> = {
  'wat-kost-managed-it-mkb': {
    en: 'managed-it-cost-per-user',
    nl: 'wat-kost-managed-it-mkb',
  },
  'nis2-voor-het-mkb': {
    en: 'nis2-for-smbs',
    nl: 'nis2-voor-het-mkb',
  },
  'it-partner-kiezen-mkb': {
    en: 'choosing-an-it-partner-checklist',
    nl: 'it-partner-kiezen-mkb',
  },
  'cloud-of-on-premise': {
    en: 'cloud-or-on-premise',
    nl: 'cloud-of-on-premise',
  },
  'ransomware-voorkomen-mkb': {
    en: 'preventing-ransomware-smb',
    nl: 'ransomware-voorkomen-mkb',
  },
  'bedrijfsprocessen-automatiseren': {
    en: 'automating-business-processes',
    nl: 'bedrijfsprocessen-automatiseren',
  },
  'nis2-checklist-mkb': {
    en: 'nis2-checklist-smb',
    nl: 'nis2-checklist-mkb',
  },
  'wat-kost-it-beheer-per-werkplek': {
    en: 'it-workplace-management-cost',
    nl: 'wat-kost-it-beheer-per-werkplek',
  },
  'chatbot-laten-maken': {
    en: 'chatbot-development-cost',
    nl: 'chatbot-laten-maken',
  },
  'mfa-instellen-microsoft-365': {
    en: 'microsoft-365-mfa-setup-guide',
    nl: 'mfa-instellen-microsoft-365',
  },
  'dmarc-spf-dkim-mkb': {
    en: 'spf-dkim-dmarc-setup-guide',
    nl: 'dmarc-spf-dkim-mkb',
  },
  'datalek-melden-mkb': {
    en: 'data-breach-notification-guide',
    nl: 'datalek-melden-mkb',
  },
  'wachtwoordmanager-mkb': {
    en: 'password-manager-smb-guide',
    nl: 'wachtwoordmanager-mkb',
  },
  'cloud-migratie-mkb': {
    en: 'cloud-migration-guide-smb',
    nl: 'cloud-migratie-mkb',
  },
  'maatwerk-software-laten-ontwikkelen': {
    en: 'custom-software-development-cost',
    nl: 'maatwerk-software-laten-ontwikkelen',
  },
  'sql-server-2016-einde-ondersteuning': {
    en: 'sql-server-2016-end-of-support',
    nl: 'sql-server-2016-einde-ondersteuning',
  },
  'legacy-software-vervangen-mkb': {
    en: 'legacy-software-replacement-guide',
    nl: 'legacy-software-vervangen-mkb',
  },
  'webshop-laten-maken': {
    en: 'webshop-development-cost',
    nl: 'webshop-laten-maken',
  },
  'bewaartermijn-klantgegevens-avg': {
    en: 'gdpr-data-retention-guide',
    nl: 'bewaartermijn-klantgegevens-avg',
  },
  'factuurverwerking-automatiseren': {
    en: 'invoice-processing-automation',
    nl: 'factuurverwerking-automatiseren',
  },
  'uitwijkplan-mkb': {
    en: 'it-disaster-recovery-plan',
    nl: 'uitwijkplan-mkb',
  },
  'windows-10-einde-ondersteuning-mkb': {
    en: 'windows-10-end-of-support-guide',
    nl: 'windows-10-einde-ondersteuning-mkb',
  },
  'data-migratie-mkb': {
    en: 'database-migration-guide',
    nl: 'data-migratie-mkb',
  },
  'systemen-koppelen-mkb': {
    en: 'business-system-integration-guide',
    nl: 'systemen-koppelen-mkb',
  },
  'vendor-lock-in-voorkomen': {
    en: 'avoiding-vendor-lock-in',
    nl: 'vendor-lock-in-voorkomen',
  },
  'it-servicedesk-uitbesteden-mkb': {
    en: 'it-help-desk-outsourcing',
    nl: 'it-servicedesk-uitbesteden-mkb',
  },
  'microsoft-365-beheer-uitbesteden': {
    en: 'microsoft-365-management-outsourcing',
    nl: 'microsoft-365-beheer-uitbesteden',
  },
  'databasebeheer-uitbesteden': {
    en: 'outsourced-database-management',
    nl: 'databasebeheer-uitbesteden',
  },
  'ai-implementatie-mkb': {
    en: 'ai-implementation-guide-smb',
    nl: 'ai-implementatie-mkb',
  },
};

/**
 * Reverse mapping (localized slug → internal slug) for URL parsing.
 * The internal slug maps to itself so an internal slug in a URL keeps working.
 */
const createReverseMap = (): Record<string, string> => {
  const reverseMap: Record<string, string> = {};

  Object.entries(articleSlugMap).forEach(([internalSlug, localizedSlugs]) => {
    reverseMap[localizedSlugs.en] = internalSlug;
    reverseMap[localizedSlugs.nl] = internalSlug;
    reverseMap[internalSlug] = internalSlug;
  });

  return reverseMap;
};

export const reverseArticleSlugMap = createReverseMap();

/**
 * Get the URL slug for an article in a given language.
 * Unmapped articles use the same slug in both languages.
 */
export function getLocalizedArticleSlug(internalSlug: string, language: 'en' | 'nl'): string {
  return articleSlugMap[internalSlug]?.[language] || internalSlug;
}

/**
 * Resolve a slug taken from the URL back to the article's internal slug.
 */
export function getInternalArticleSlug(localizedSlug: string): string {
  return reverseArticleSlugMap[localizedSlug] || localizedSlug;
}
